import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import moment from "moment";

import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import MDInput from "admin/components/MDInput";
import MDButton from "admin/components/MDButton";
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";
import MapBuilderEditor from "./MapBuilderEditor";

const API_URL = process.env.REACT_APP_APIHOST || "http://localhost:3033";

const EMPTY_FORM = { title: "", description: "", date: "", address: "", lat: "", lng: "" };

export default function MapBuilder() {
    const [maps, setMaps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [creating, setCreating] = useState(false);
    const [activeMap, setActiveMap] = useState(null); // { id, title, lat, lng, layers }

    const fetchMaps = useCallback(async () => {
        try {
            const r = await axios.get(API_URL + "/map-builder");
            if (r.data.status) setMaps(r.data.maps || []);
        } catch (_) {}
        setLoading(false);
    }, []);

    useEffect(() => { fetchMaps(); }, [fetchMaps]);

    const handleAddressChange = (val) => {
        setForm(f => ({ ...f, address: val }));
        if (val.length < 3) return;
        clearTimeout(window._mbGeoTimer);
        window._mbGeoTimer = setTimeout(async () => {
            try {
                const r = await fetch("https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" + encodeURIComponent(val));
                const d = await r.json();
                if (d?.[0]) setForm(f => ({ ...f, lat: parseFloat(d[0].lat).toFixed(5), lng: parseFloat(d[0].lon).toFixed(5) }));
            } catch (_) {}
        }, 600);
    };

    const handleCreate = async () => {
        if (!form.title) return alert("Title required");
        setCreating(true);
        try {
            const r = await axios.post(API_URL + "/map-builder", form);
            if (r.data.status) {
                setShowForm(false);
                setForm(EMPTY_FORM);
                const m = r.data.map;
                setActiveMap(m);
                fetchMaps();
            }
        } catch (e) { alert(e.response?.data?.error || e.message); }
        setCreating(false);
    };

    const openMap = async (id) => {
        try {
            const r = await axios.get(API_URL + "/map-builder/" + id);
            if (r.data.status) setActiveMap(r.data.map);
        } catch (_) {}
    };

    const deleteMap = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm("Delete this map?")) return;
        try {
            await axios.delete(API_URL + "/map-builder/" + id);
            setMaps(m => m.filter(x => String(x._id) !== String(id)));
        } catch (_) {}
    };

    if (activeMap) {
        return (
            <MapBuilderEditor
                map={activeMap}
                onBack={() => { setActiveMap(null); fetchMaps(); }}
                apiUrl={API_URL}
            />
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={3} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <MDTypography variant="h5" fontWeight="medium" sx={{ color: "#fff !important" }}>
                                <Icon sx={{ verticalAlign: "middle", mr: 1, color: "#C29717" }}>map</Icon>
                                Map Builder
                            </MDTypography>
                            <MDButton color="warning" onClick={() => setShowForm(true)} startIcon={<Icon>add</Icon>}>
                                New Map
                            </MDButton>
                        </MDBox>
                    </Grid>

                    {/* Map cards */}
                    {loading ? (
                        <Grid item xs={12}>
                            <MDTypography variant="body2" sx={{ color: "#888 !important" }}>Loading…</MDTypography>
                        </Grid>
                    ) : maps.length === 0 ? (
                        <Grid item xs={12}>
                            <Card>
                                <MDBox p={5} textAlign="center">
                                    <Icon sx={{ fontSize: "48px !important", color: "#444", display: "block", mb: 2 }}>map</Icon>
                                    <MDTypography variant="body2" sx={{ color: "#666 !important" }}>
                                        No maps yet. Create your first intelligence map.
                                    </MDTypography>
                                </MDBox>
                            </Card>
                        </Grid>
                    ) : (
                        maps.map(m => (
                            <Grid item xs={12} sm={6} md={4} key={String(m._id)}>
                                <Card sx={{
                                    cursor: "pointer", transition: "border-color 0.2s",
                                    border: "1px solid rgba(255,255,255,0.06)",
                                    "&:hover": { borderColor: "rgba(194,151,23,0.4)" },
                                }} onClick={() => openMap(String(m._id))}>
                                    <MDBox p={2.5}>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                            <MDTypography variant="h6" fontWeight="medium" sx={{ color: "#fff !important", fontSize: "14px", flex: 1 }}>
                                                {m.title}
                                            </MDTypography>
                                            <IconButton size="small" onClick={(e) => deleteMap(String(m._id), e)}
                                                sx={{ ml: 1, color: "#555", "&:hover": { color: "#ef4444" } }}>
                                                <Icon fontSize="small">delete</Icon>
                                            </IconButton>
                                        </MDBox>
                                        {m.description && (
                                            <MDTypography variant="caption" sx={{ color: "#888 !important", display: "block", mb: 1 }}>
                                                {m.description}
                                            </MDTypography>
                                        )}
                                        <MDBox display="flex" gap={2} mt={1.5} flexWrap="wrap">
                                            {m.date && (
                                                <MDTypography variant="caption" sx={{ color: "#C29717 !important" }}>
                                                    <Icon sx={{ fontSize: "12px !important", verticalAlign: "middle", mr: 0.5 }}>calendar_today</Icon>
                                                    {moment(m.date).format("DD MMM YYYY")}
                                                </MDTypography>
                                            )}
                                            {(m.lat || m.address) && (
                                                <MDTypography variant="caption" sx={{ color: "#666 !important" }}>
                                                    <Icon sx={{ fontSize: "12px !important", verticalAlign: "middle", mr: 0.5 }}>location_on</Icon>
                                                    {m.address || `${(+m.lat).toFixed(3)}N, ${(+m.lng).toFixed(3)}E`}
                                                </MDTypography>
                                            )}
                                            <MDTypography variant="caption" sx={{ color: "#555 !important", marginLeft: "auto" }}>
                                                {(m.layers || []).length} layers
                                            </MDTypography>
                                        </MDBox>
                                        <MDTypography variant="caption" sx={{ color: "#444 !important", display: "block", mt: 0.5 }}>
                                            {moment(m.updatedAt || m.createdAt).fromNow()}
                                        </MDTypography>
                                    </MDBox>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            </MDBox>

            {/* New map dialog */}
            <Dialog open={showForm} onClose={() => setShowForm(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { background: "#111213", border: "1px solid rgba(255,255,255,0.08)" } }}>
                <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid rgba(255,255,255,0.06)", pb: 1.5 }}>
                    <Icon sx={{ verticalAlign: "middle", mr: 1, color: "#C29717" }}>add_location_alt</Icon>
                    New Intelligence Map
                    <IconButton onClick={() => setShowForm(false)} sx={{ position: "absolute", right: 8, top: 8, color: "#666" }}>
                        <Icon>close</Icon>
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: "20px !important" }}>
                    <MDInput label="Map title" fullWidth value={form.title}
                        onChange={e => setForm(f => ({ ...f, title: e.target.value }))} sx={{ mb: 2 }} />
                    <MDInput label="Description (optional)" fullWidth multiline rows={2} value={form.description}
                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))} sx={{ mb: 2 }} />
                    <MDInput type="date" label="Date" fullWidth value={form.date}
                        onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                        InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
                    <MDInput label="Location address" fullWidth value={form.address}
                        onChange={e => handleAddressChange(e.target.value)} sx={{ mb: 1 }}
                        placeholder="Type address — coordinates auto-fill" />
                    <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <MDInput label="Latitude" fullWidth value={form.lat}
                                onChange={e => setForm(f => ({ ...f, lat: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6}>
                            <MDInput label="Longitude" fullWidth value={form.lng}
                                onChange={e => setForm(f => ({ ...f, lng: e.target.value }))} />
                        </Grid>
                    </Grid>
                    <MDButton color="warning" fullWidth onClick={handleCreate} disabled={creating}>
                        {creating ? "Creating…" : "Build Map"}
                    </MDButton>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
